<script setup lang="ts">
useSeoMeta({
  title: 'Projects — Shakir Abdulrahim',
  ogTitle: 'Projects — Shakir Abdulrahim',
  description: 'Portfolio of web applications, hardware projects, and open-source contributions.',
})

const categories = [
  { value: '', label: 'All' },
  { value: 'web', label: 'Web' },
  { value: 'gov', label: 'Government' },
  { value: 'ai', label: 'AI' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'iot', label: 'IoT' },
  { value: 'oss', label: 'Open Source' },
]

const activeCategory = ref('')
const { imageUrl } = useDirectusImage()

const { data: projects } = await useFetch('/api/projects', {
  default: () => [],
})

const filteredProjects = computed(() => {
  if (!activeCategory.value) return projects.value
  return (projects.value as any[]).filter((p: any) => p.category === activeCategory.value)
})
</script>

<template>
  <div class="py-24">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <p class="mb-4 text-sm font-medium tracking-widest text-[var(--color-amber)] uppercase">
        Projects
      </p>
      <h1 class="mb-6 text-4xl font-bold text-[var(--color-text)] sm:text-5xl">
        Prove it. Show the work.
      </h1>
      <p class="mb-10 text-lg text-[var(--color-text-muted)]">
        A decade of shipping — from government platforms to bare-metal microcontrollers.
      </p>

      <!-- Category Filter -->
      <div class="mb-10 flex flex-wrap gap-2">
        <button
          v-for="cat in categories"
          :key="cat.value"
          class="rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200"
          :class="activeCategory === cat.value
            ? 'bg-[var(--color-amber)] text-[var(--color-dark-base)]'
            : 'border border-[var(--color-card-border)] text-[var(--color-text-muted)] hover:border-[var(--color-amber)]/30 hover:text-[var(--color-text)]'"
          @click="activeCategory = cat.value"
        >
          {{ cat.label }}
        </button>
      </div>

      <!-- Projects Grid -->
      <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <NuxtLink
          v-for="project in filteredProjects"
          :key="(project as any).id"
          :to="`/projects/${(project as any).slug}`"
          class="group relative overflow-hidden rounded-xl border border-[var(--color-card-border)] bg-[var(--color-card-bg)] transition-all duration-300 hover:border-[var(--color-amber)]/20 hover:shadow-[0_0_40px_rgba(250,185,91,0.05)]"
        >
          <div class="pointer-events-none absolute -inset-px rounded-xl bg-gradient-to-b from-[var(--color-amber)]/0 to-transparent opacity-0 transition-opacity duration-500 group-hover:from-[var(--color-amber)]/5 group-hover:opacity-100" />

          <div v-if="(project as any).image" class="relative aspect-[16/9] overflow-hidden">
            <img
              :src="imageUrl((project as any).image, { width: 640, quality: 80 })!"
              :alt="(project as any).title"
              class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            >
          </div>

          <div class="relative p-6">
            <div class="mb-3 flex items-center gap-2">
              <span class="rounded-full bg-[var(--color-amber)]/10 px-2.5 py-0.5 text-xs font-semibold text-[var(--color-amber)]">
                {{ (project as any).category }}
              </span>
              <span v-if="(project as any).featured" class="rounded-full bg-[var(--color-steel-blue)]/10 px-2.5 py-0.5 text-xs font-semibold text-[var(--color-steel-blue)]">
                Featured
              </span>
            </div>
            <h3 class="mb-2 text-lg font-semibold text-[var(--color-text)] transition-colors group-hover:text-[var(--color-amber)]">
              {{ (project as any).title }}
            </h3>
            <p class="mb-4 line-clamp-2 text-sm text-[var(--color-text-muted)]">
              {{ (project as any).description }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="tag in ((project as any).tags || []).slice(0, 4)"
                :key="tag"
                class="rounded-md border border-[var(--color-card-border)] bg-[var(--color-surface-hover)] px-2 py-0.5 text-xs text-[var(--color-text-muted)]"
              >
                {{ tag }}
              </span>
            </div>
          </div>
        </NuxtLink>
      </div>

      <p v-if="!filteredProjects?.length" class="mt-10 text-center text-[var(--color-text-muted)]">
        No projects in this category yet.
      </p>
    </div>
  </div>
</template>
